import { useState } from 'react';

/**
 * SubscriptionGate — wraps action buttons for expired trial users.
 *
 * Usage:
 *   <SubscriptionGate isSubscribed={isSubscribed}>
 *     <button onClick={handleGenerate}>Generate Response</button>
 *   </SubscriptionGate>
 *
 * If subscribed: renders children normally.
 * If not subscribed: renders children as disabled with an upgrade prompt on click.
 */
export default function SubscriptionGate({ isSubscribed, children, feature }) {
  const [showPrompt, setShowPrompt] = useState(false);

  if (isSubscribed) return children;

  return (
    <>
      <div
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowPrompt(true); }}
        style={{ display: 'inline-block', cursor: 'pointer' }}
      >
        <div style={{ opacity: 0.5, pointerEvents: 'none' }}>
          {children}
        </div>
      </div>

      {showPrompt && (
        <div className="modal-overlay" onClick={() => setShowPrompt(false)}>
          <div className="modal-content card" onClick={e => e.stopPropagation()} style={{ maxWidth: 420, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
            <h2 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 8px', color: 'var(--text)' }}>
              Subscribe to continue
            </h2>
            <p style={{ fontSize: 14, color: 'var(--text-light)', lineHeight: 1.6, margin: '0 0 8px' }}>
              Your free trial has ended.
              {feature ? ` ${feature} requires` : ' This action requires'} an active subscription.
            </p>
            <p style={{ fontSize: 13, color: 'var(--text-light)', margin: '0 0 24px' }}>
              Your data is safe and waiting. Subscribe to pick up right where you left off.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button className="btn btn-ghost" onClick={() => setShowPrompt(false)}>
                Maybe later
              </button>
              <a href="/billing" className="btn btn-primary" style={{ textDecoration: 'none' }}>
                View plans
              </a>
            </div>
            <p style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 16 }}>
              Early adopter pricing: £20/mo · Price locked in forever
            </p>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * useSubscriptionStatus — hook to check subscription status.
 *
 * Returns { isSubscribed, subscriptionStatus }
 * Use with SubscriptionGate or to conditionally render UI.
 */
export function useSubscriptionStatus(user) {
  const status = user?.subscription_status || '';
  const isSubscribed = ['active', 'trialing'].includes(status);
  return { isSubscribed, subscriptionStatus: status };
}

/**
 * is403 — check if an error is a subscription 403.
 * Use in catch blocks to show a friendly message instead of generic errors.
 *
 * Usage:
 *   catch (err) {
 *     if (is403(err)) showToast('Subscribe to use this feature');
 *     else showToast(err.message);
 *   }
 */
export function is403(err) {
  return err?.response?.status === 403 || err?.status === 403;
}
